import {getCorrectDayTwoDayDelivery} from '@optimaxdev/utils';

import {createSelector} from '@reduxjs/toolkit';

import {SHIPPING_METHODS_CODES} from 'desktop/pages/checkout/constants/deliveryStep';
import {ApplicationStoreType} from 'desktop/store';
import {ShippingMethodsListType, ShippingMethodType} from 'types/shippingMethodsType';

import {MODULE_KEY_CHECKOUT} from '../constants';

/**
 * Get delivery step shipping methods
 *
 * @param {ApplicationStoreType} store - current store
 * @return {ApplicationStoreType} methods
 */
export const getDeliveryStepShippingMethods = (
    store: ApplicationStoreType,
): ShippingMethodsListType => store[MODULE_KEY_CHECKOUT].deliveryStep.shippingMethods;

/**
 * Get shipping methods list
 *
 * @param {ApplicationStoreType} store - current store
 * @return {Array<ShippingMethodType>} method list
 */
export const getShippingMethodList = (store: ApplicationStoreType): Array<ShippingMethodType> =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingMethods.list;

/**
 * Get shipping methods count
 *
 * @param {ApplicationStoreType} store - current store
 * @return {number} selected method
 */
export const getShippingMethodsCount = (store: ApplicationStoreType): number =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingMethods.list.length;

/**
 * Get shipping method error
 *
 * @param {ApplicationStoreType} store - current store
 * @return {number} selected method
 */
export const getShippingMethodsError = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingMethods.error;

/**
 * Get shipping methods status
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} method status
 */
export const getShippingMethodsStatus = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingMethods.status;

/**
 * Get selected shipping method code
 *
 * @param {ApplicationStoreType} store - current store
 * @return {string} selected method
 */
export const getSelectedShippingMethodCode = (store: ApplicationStoreType): string =>
    store[MODULE_KEY_CHECKOUT].deliveryStep.shippingMethods.selected;

/**
 * Get next day shipping method
 *
 * @param {ApplicationStoreType} store - current store
 * @returns {ShippingMethodType | undefined} - next day shipping method
 */
export const getNextDayShippingMethod = createSelector(
    getShippingMethodList,
    (list: Array<ShippingMethodType>) =>
        list.find(({code}) => code === SHIPPING_METHODS_CODES.NEXT_DAY),
);

/**
 * Get hits of shipping methods
 *
 * @param {ApplicationStoreType} store - current store
 * @return {Array<string>} hits
 */
export const getShippingMethodsHits = createSelector(
    getShippingMethodList,
    getNextDayShippingMethod,
    (shippingMethods, nextDayShippingMethod) => {
        const twoDayDeliveryDay =
            nextDayShippingMethod?.additionalData &&
            getCorrectDayTwoDayDelivery(nextDayShippingMethod?.additionalData?.day);
        let filteredShippingMethods = shippingMethods;

        if (nextDayShippingMethod) {
            filteredShippingMethods = shippingMethods.filter(
                ({code}) => code !== SHIPPING_METHODS_CODES.EXPEDITED,
            );
        }

        return filteredShippingMethods.reduce((acc: Array<string>, method): Array<string> => {
            if (method.description)
                acc.push(`${acc.length > 0 ? ' ' : ''}${method.description.split(' b')[0]} BD`);

            if (method.code === SHIPPING_METHODS_CODES.NEXT_DAY)
                acc.push(` 1 BD - ${twoDayDeliveryDay === 'tomorrow' ? 'Tomorrow' : 'Other'}`);

            return acc;
        }, []);
    },
);

/**
 * True if shipping methods === 1 or there is a mailbox in the address fields
 *
 * @param {ApplicationStoreType} store - current store
 * @returns {boolean} true if shipping methods === 1 or there is a mailbox in the address fields
 */
export const showSingleShippingMethod = createSelector(
    getShippingMethodsCount,
    (methodsCount): boolean => methodsCount === 1,
);

/**
 * Get selected shipping method
 *
 * @param {ApplicationStoreType} store - current store
 * @returns {ShippingMethodType} - selected shipping method
 */
export const getSelectedShippingMethod = createSelector(
    getSelectedShippingMethodCode,
    getShippingMethodList,
    (code: string, list: Array<ShippingMethodType>) =>
        list.find(shippingMethod => shippingMethod.code === code),
);
